import { useSelector } from 'react-redux';

import { cn } from 'helpers/classnames';
import type { BoxInfo, CustomerInfo } from 'types/balder';
import { UPS_FIND_STORE_URL } from 'constants/appConstants';
import { PROVIDE_LABEL_OPTIONS } from 'constants/exchanges';
import { selectReturnItemCount } from 'selectors/returns';
import useMartyContext from 'hooks/useMartyContext';
import ReturnInstructionsSection from 'components/account/Returns/ReturnInstructionsSection';
import ReturnLabelOption from 'components/account/Returns/ReturnLabelOption';
import WarningFilledSmallIcon from 'components/common/IconsV2/WarningFilledSmallIcon';
import StoreIcon from 'images/zappos/store-icon.svg';
import External from 'images/zappos/external.svg';

interface LabellessOptionProps {
  boxInfo: BoxInfo;
  className: string;
  customerInfo: CustomerInfo;
  displayOnly: boolean;
  labelDelivery: string;
  showInstructions: boolean;
  showLabellessReturns: boolean;
  onReturnMethodSelect: () => void;
  showInstructionsHandler: () => void;
}

const LabellessOption = ({
  className,
  customerInfo,
  displayOnly,
  labelDelivery,
  showInstructions,
  showLabellessReturns,
  onReturnMethodSelect,
  showInstructionsHandler
}: LabellessOptionProps) => {
  const {
    marketplace: { dataMaskPiiElements }
  } = useMartyContext();
  const returnItemCount = useSelector(selectReturnItemCount);

  if (!showLabellessReturns) {
    return null;
  }

  const checked = labelDelivery === PROVIDE_LABEL_OPTIONS.LABELLESS;

  const extraInfo = (
    <div className="mt-4 text-sm italic zappos:body-s">
      *Participating UPS locations include The UPS Store only. It does not include UPS Access Point™, UPS Customer Centre, UPS Drop Box, Retail
      Chains, Authorized Shipping Outlet, and UPS Authorized Service Providers.
    </div>
  );

  const returnInstructionsSection = (
    <ReturnInstructionsSection
      checked={checked}
      displayOnly={displayOnly}
      extraInfo={extraInfo}
      showInstructions={showInstructions}
      showInstructionsHandler={showInstructionsHandler}
      value={PROVIDE_LABEL_OPTIONS.LABELLESS}
      storeIcon={StoreIcon}
    />
  );

  const optionInformation = (
    <>
      <span className="mt-2 text-xs font-bold text-gray-600 zappos:body-m-bold">Shipping box required and no label needed</span>
      <div className={cn('text-xs zappos:body-m', { 'block': checked, 'hidden md:hidden': !checked })}>
        {checked && returnItemCount > 1 ? (
          <div className="my-2 flex gap-x-3">
            <div className="relative top-1 text-icon-warning-light">
              <WarningFilledSmallIcon size={16} />
            </div>
            <p>
              <span className="font-bold zappos:body-m-bold">This will only generate one code.</span> If you have multiple boxes, process a separate
              return for additional items and get a new code.
            </p>
          </div>
        ) : null}
        Show your return code at participating{' '}
        <a href={UPS_FIND_STORE_URL} target="_blank" rel="noopener noreferrer">
          The UPS Store*
          <img className="inline-block" src={External} alt={'external'} />
        </a>{' '}
        locations and we will take care of the rest.
      </div>
      {checked && !displayOnly && customerInfo?.email && (
        <div className="mb-2 mt-3">
          <p className="text-xs font-semibold zappos:body-m-medium" data-cs-mask={dataMaskPiiElements}>
            Return code will be sent to: <span className="text-xs font-medium zappos:body-m-medium">{customerInfo.email}</span>
          </p>
        </div>
      )}
    </>
  );

  const labelButtonsGroup = (
    <div className="mt-3">
      <a href={UPS_FIND_STORE_URL} target="_blank" rel="noopener noreferrer" className="zappos:body-sm text-sm">
        Find Participating UPS Stores <img className="inline-block" src={External} alt={'external'} />
      </a>
    </div>
  );

  return (
    <ReturnLabelOption
      checked={checked}
      children={returnInstructionsSection}
      icon={StoreIcon}
      id={PROVIDE_LABEL_OPTIONS.LABELLESS}
      optionTitle="Drop-Off"
      optionInformation={optionInformation}
      onReturnMethodSelect={onReturnMethodSelect}
      value={PROVIDE_LABEL_OPTIONS.LABELLESS}
      displayOnly={displayOnly}
      labelButtonsGroup={labelButtonsGroup}
      className={className}
    />
  );
};

export default LabellessOption;
